import { DateTime } from 'luxon';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { LoaderService } from '../loader.service';
import { CommonDataProvider } from './common.data-provider';
import { CompanyService } from '../company.service';
import {
  DeliveryDaysModel,
  DeliverySlot,
  DeliverySlotTemplatesForWeekday
} from '../../models/delivery-days.model';
import { catchError, map } from 'rxjs/operators';
import { timestampToStringFormat } from '../../../shared/helpers/helpers';

@Injectable()
export class DeliveryDaysDataProvider extends CommonDataProvider<DeliveryDaysModel> {
  constructor(
    http: HttpClient,
    private companyService: CompanyService,
    loaderService: LoaderService
  ) {
    super(http, loaderService);
  }

  getGeneratedSlots(restaurantId: number, from: number): Observable<DeliveryDaysModel[]> {
    const fromDate = timestampToStringFormat(from);
    return this.http
      .get(`${environment.apiUrl}/admin/restaurants/${restaurantId}/delivery-days?from=${fromDate}`)
      .pipe(
        catchError(this.handleError),
        map((r: any) => r.data as DeliveryDaysModel[])
      );
  }

  createDeliverySlot(restaurantId: number, slot: DeliverySlot): Observable<DeliverySlot> {
    return this.postAction<DeliverySlot>(
      `${environment.apiUrl}/admin/restaurants/${restaurantId}/delivery-slots`,
      slot
    );
  }

  updateDeliverySlot(restaurantId: number, slot: DeliverySlot) {
    return this.putAction(
      `${environment.apiUrl}/admin/restaurants/${restaurantId}/delivery-slots/${slot.id}`,
      slot
    );
  }

  deleteDeliverySlot(restaurantId: number, slotId: number) {
    return this.http.request(
      'delete',
      `${environment.apiUrl}/admin/restaurants/${restaurantId}/delivery-slots/${slotId}`
    );
  }

  generateDeliverySlotsByTemplates(
    restaurantId: number,
    from: number,
    to: number
  ): Observable<DeliveryDaysModel[]> {
    const fromDate = timestampToStringFormat(from);
    const toDate = timestampToStringFormat(to);
    return this.postAction<DeliveryDaysModel[]>(
      `${environment.apiUrl}/admin/restaurants/${restaurantId}/delivery-slots/generate?from=${fromDate}&to=${toDate}`
    );
  }

  getTemplatesByRestaurant(restaurantId: number): Observable<DeliverySlotTemplatesForWeekday[]> {
    return this.http
      .get<{
        data: DeliverySlotTemplatesForWeekday[];
      }>(`${environment.apiUrl}/admin/restaurants/${restaurantId}/delivery-day-templates`)
      .pipe(
        map((response: { data: DeliverySlotTemplatesForWeekday[] }) => response.data),
        catchError(this.handleError)
      );
  }

  saveTemplatesForRestaurant(data: DeliverySlotTemplatesForWeekday[]) {
    return this.putAction(`${environment.apiUrl}/admin/delivery-day-templates`, data);
  }

  deleteRangeTemplates(
    restaurantId: number,
    from: Date | DateTime,
    to: Date | DateTime
  ): Observable<{ blocking_order_ids: number[]; delivery_days: DeliveryDaysModel[] }> {
    const fromDate =
      from instanceof Date
        ? DateTime.fromJSDate(from).toFormat('yyyy-LL-dd')
        : from.toFormat('yyyy-LL-dd');
    let url = `${environment.apiUrl}/admin/restaurants/${restaurantId}/delivery-slots?from=${fromDate}`;
    if (to) {
      const toDate =
        to instanceof Date
          ? DateTime.fromJSDate(to).toFormat('yyyy-LL-dd')
          : to.toFormat('yyyy-LL-dd');
      url = `${url}&to=${toDate}`;
    }
    return this.deleteAction<{ blocking_order_ids: number[]; delivery_days: DeliveryDaysModel[] }>(
      url
    ).pipe(catchError(this.handleError));
  }
}
